<template>
  <div>
    <base-modal :modalTitle="'Agent Detail'" :loaded="loaded" :saveDisabled="!editable" v-on:save="save()" v-on:close="close()" class="modal-lg">
      <template>
        <div class="row">
          <h1>{{ agent.npn }} - {{ agent.name }}</h1>
          <div v-if="!editable" class="alert alert-warning"><i class="fa fa-warning"></i> Agent's profile is linked to a 3rd party service and cannot be edited.</div>

          <FormulateForm :errors="fieldErrors">
            <div class="col-xs-12 col-md-6">
              <h2>Contact Details</h2>
              <FormulateInput type="text" name="npn" v-model="agent.npn" label="NPN" validation="required" :disabled="!editable" />
              <FormulateInput type="text" name="name" v-model="agent.name" label="Name" validation="required" :disabled="!editable" />
              <FormulateInput type="text" name="email" v-model="agent.email" label="Email" validation="required" :disabled="!editable" />
              <FormulateInput type="text" name="phone" v-model="agent.phone" label="Phone" validation="required" :disabled="!editable" />
            </div>
            <div class="col-xs-12 col-md-6">
              <h2>Status: {{ agent.status.code | uppercase }}</h2>
              <FormulateInput type="checkbox" v-model="agent.is_active" label="Active" :disabled="!editable" />

              <div class="input-group col-xs-12">
                <div><b>LeadScorz Id:</b> {{ agent.id }} <a role="button" @click="copyId"><i class="fa fa-copy"></i></a> <span v-if="copiedId" class="copied-feedback"></span></div>
              </div>
              <template v-if="agent.appointments_profile.accept_appointments">
                <h2>Appointments Settings</h2>
                <h5>Supported Appointment Types</h5>
                <span v-for="appointment_type in agent.appointments_profile.supported_appointment_types" class="label label-primary">{{ appointment_type }}</span>
                <h5 v-if="agent.appointments_profile.daily_capacity != null">Daily capacity: {{ agent.appointments_profile.daily_capacity }}</h5>
              </template>
              <h2>Tags</h2>
              <tags-widget :tags="agent.tags" @removeTag="removeTag"  @addTag="addTag" :readOnly="!editable"></tags-widget>
            </div>
          </FormulateForm>
        </div>
        <div v-if="agentId" class="row">
          <h2>License Status</h2>
          <div class="col-xs-12">
            <div v-for="license in agent.active_licenses" class="box col-xs-6">
              <h4>{{ license.carrier }} ({{ license.year }})</h4>
              <span class="label label-default" v-for="state in license.states">{{ state }}</span>
              </ul>
            </div>
          </div>
        </div>
        <div v-if="agentId" class="row">
          <h2>Historical Performance</h2>
          <div class="col-xs-12">

            <div class="col-xs-3">
              <div class="box">
                <div class="panel stats">
                  <div class="panel-header">
                    Active Appointments
                  </div>
                  <div class="panel-body">
                    <div class="metrics-banner-text text-center">
                      {{ agent.historical_performance.active_appointments }}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-xs-3">
              <div class="box">
                <div class="panel stats">
                  <div class="panel-header">
                    Pending Confirmation
                  </div>
                  <div class="panel-body">
                    <div class="metrics-banner-text text-center">
                      {{ agent.historical_performance.pending_confirmation_appointments }}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-xs-3">
              <div class="box">
                <div class="panel stats">
                  <div class="panel-header">
                    Confirmation Rate
                  </div>
                  <div class="panel-body">
                    <div class="metrics-banner-text text-center">
                      {{ agent.historical_performance.confirmation_rate }}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-xs-3">
              <div class="box">
                <div class="panel stats">
                  <div class="panel-header">
                    Completion Rate
                  </div>
                  <div class="panel-body">
                    <div class="metrics-banner-text text-center">
                      {{ agent.historical_performance.completion_rate }}
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>

        <div v-if="agentId" class="row">
          <h2>Appointments</h2>
          <div class="col-xs-12">
            <table class="table table-condensed">
              <thead>
                <tr>
                  <th>Id</th><th>Created At</th><th>Appointment Type</th><th>Status</th><th>Start At</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="appointment in agentAppointments">
                  <td>{{ appointment.id }}</td>
                  <td>{{ appointment.created | formatTimestamp }}</td>
                  <td>{{ appointment.appointment_type }}</td>
                  <td>{{ appointment.current_status }}</td>
                  <td>{{ appointment.start_timestamp | formatTimestamp }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div v-if="agentId && agent.appointments_profile.consolidated_slots" class="row">
          <h2>Available Slots</h2>
          <div class="col-xs-12">
            <table class="table table-condensed table-bordered table-hover">
              <thead>
                <tr>
                  <th>Time</th>
                  <th v-for="day in agent.appointments_profile.consolidated_slots.header">{{ day }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="slots in agent.appointments_profile.consolidated_slots.rows">
                  <td>{{ slots['key'] }}</td>
                  <template v-for="day in agent.appointments_profile.consolidated_slots.header">
                    <td v-if="slots['days'][day]">
                      <span v-for="appointmentType in slots['days'][day]" class="label" :class="getLabelColor(appointmentType)"><i :title="getAltText(appointmentType)" class="fa fa-lg" :class="getIcon(appointmentType)" /></span>
                    </td>
                    <td v-else><span>-</span></td>
                  </template>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div v-if="agentId && agent.appointments_profile.regular_schedule" class="row">
          <h2>Regular Schedule</h2>
          <div class="col-xs-12">
            <p>
              Timezone: {{ agent.appointments_profile.timezone }}
             </p>
             <table class="table table-condensed table-hover">
               <tbody>
                 <tr v-for="weekday in agent.appointments_profile.regular_schedule">
                   <th>{{weekday.day}}</th>
                   <td>
                     <span class="label label-default" v-for="(types, slot) in weekday.slots">
                     {{ slot }} <span class="label" :class="getLabelColor(type)" v-for="type in types"><i :title="getAltText(type)" class="fa fa-lg" :class="getIcon(type)" /></span>
                     </span>
                   </td>
                 </tr>
               </tbody>
             </table>
          </div>
        </div>

        <div v-if="agentId" class="row">
          <h2>ZIP Codes definition</h2>
          <div v-for="def_by_type in agent.appointments_profile.travel_zipcodes" class="col-xs-12">
            <h4>{{ def_by_type.label }}</h4>
            <table class="table table-condensed">
              <thead>
                <tr>
                  <th>State</th><th>ZIP Codes</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="def_by_state in def_by_type.states">
                  <td>{{ def_by_state.state }}</td>
                  <td>{{ def_by_state.zipcodes.length > 20 ? def_by_state.zipcodes.slice(0, 10).join(', ') + " ... (total: " +  (def_by_state.zipcodes.length - 10).toString() +")" :  def_by_state.zipcodes.join(', ') }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="col-xs-12">
            <h4>Check ZIP Code coverage</h4>
            <div class="col-xs-4">
              <FormulateForm @submit="checkZipcode">
                <div class="zipcode-check">
                  <FormulateInput type="text" placeholder="Enter ZIP code" v-model="zipcodeCheck"></FormulateInput>
                  <FormulateInput type="submit" label="Check"></FormulateInput>
                </div>
              </FormulateForm>
            </div>
            <div class="col-xs-8">
              <div class="well well-sm" v-if="zipcodeCheck && zipcodeCheckResult">
                <p v-if="zipcodeCheckResult.is_covered">Available Appointment Types in <b>{{ zipcodeCheckResult.zipcode  }}</b>: <span v-for="appointmentType in zipcodeCheckResult.appointment_types" class="label label-primary">{{ appointmentType }}</span></p>
                <p v-else ><b>{{ zipcodeCheckResult.zipcode  }}</b> is not covered</p>
              </div>
            </div>
          </div>
        </div>


      </template>
    </base-modal>
  </div>
</template>
<script>
  // Load the full build.
 var _ = require('lodash');

 import BaseModal from 'components/modals/BaseModal';
 import TagsWidget from "components/elements/TagsWidget";
 import AgentScheduleSlotModal from 'components/modals/AgentScheduleSlotModal';
 import AgentScheduleCustomSlotModal from 'components/modals/AgentScheduleCustomSlotModal';
 import LsChart from 'components/elements/PlotlyJS/LSBasicChart';

  export default {
    components: {
      BaseModal,
      TagsWidget,
      AgentScheduleSlotModal,
      AgentScheduleCustomSlotModal,
      LsChart
    },
    props: {
      agentId: String,
    },
    data () {
      return {
        editable: true,
        loaded: false,
        zipcodeCheck: '',
        zipcodeCheckResult: null,
        agent: {
          id: null,
          npn: '',
          name: '',
          email: '',
          phone: '',
          appointments_profile: {
            accept_appointments: false,
            daily_capacity: null,
            supported_appointment_types: []
          },
          is_active: false,
          status: {
            code: '-',
          },
          status_history: [],
          historical_performance: {
            active_appointments: '-',
            pending_confirmation_appointments: '-',
            confirmation_rate: '-',
            completion_rate: '-'
          },
          tags: []
        },
        agentAppointments: [],
        copiedId: false,
        fieldErrors: null,
        modal_errors:[],
      };
    },
    created () {
    },
    computed: {
      tags: function () {
        return new Set(this.agent.tags.map(t => t.id));
      },
    },
    watch: {
      agentId(newValue, oldValue) {
        if (newValue) {
          this.fetchAgent();
        }
      },
      zipcodeCheck(newValue, oldValue) {
        if (newValue.length < 5) {
          this.zipcodeCheckResult = null;
        }
        else if ((newValue != oldValue) && this.zipcodeCheckResult) {
          this.zipcodeCheckResult = null;
        }
      }
    },
    methods: {
      async fetchAgent() {
        this.loaded = false;
        const options = await API.agents.agentAPIOptions(this.agentId);
        this.editable = 'actions' in options.data && 'PUT' in options.data.actions;
        const response = await API.agents.get(this.agentId);
        this.agent = response.data;

        const appointmentsResponse = await API.agents.getAgentAppointments(this.agentId);
        this.agentAppointments = appointmentsResponse.data;

        this.loaded = true;
      },
      close() {
        this.$emit('close');
      },
      copyId() {
        var self = this;
         navigator.clipboard.writeText(this.agent.id).then(function() {
           self.copiedId = true;
           setTimeout(function () {
             self.copiedId = false;
           }, 2000);
         });
      },
      checkZipcode() {
        var self = this;
        API.agents.checkZipcode(self.agent.id, self.zipcodeCheck)
           .then((response) => {
             self.zipcodeCheckResult = response.data;
           })
      },
      save() {
        var self = this;
        if (!self.editable) {
          window.alert('This agent is linked to a 3rd party service and cannot be edited');
        }
        else {
          var req = _.cloneDeep(self.agent);
          delete req.status;
          delete req.status_history;

          API.agents.save(req)
             .then(
                function (response) {
                  self.fieldErrors = null;
                  self.modal_errors = [];
                  self.$emit('close', true);
                },
               function (errors) {
                 if ('non_field_errors' in errors.body) {
                   self.modal_errors = errors.body['non_field_errors'];
                   delete errors.body['non_field_errors'];
                 }
                 self.fieldErrors = errors.body;
             });
        }
      },
      removeTag(tag) {
        this.agent.tags = this.agent.tags.filter((t) => t != tag);
      },
      addTag(tag) {
        if (!this.tags.has(tag)) {
          this.agent.tags.push(tag);
        }
      },
        getIcon(appointmentType) {
            switch (appointmentType) {
            case 'phone-call':
                return 'fa-phone';
            case 'in-person-at-home':
                return 'fa-home';
            case 'in-person-at-office':
                return 'fa-building-o';
            case 'online':
                return 'fa-video-camera';
            default:
                return '';
            }
        },
        getAltText(appointmentType) {
            switch (appointmentType) {
            case 'phone-call':
                return 'Phone Call';
            case 'in-person-at-home':
                return 'At Home';
            case 'in-person-at-office':
                return 'At Office';
            case 'online':
                return 'Online';
            default:
                return '';
            }
        },
        getLabelColor(appointmentType) {
            switch (appointmentType) {
            case 'phone-call':
                return 'label-success';
            case 'in-person-at-home':
                return 'label-warning';
            case 'in-person-at-office':
                return 'label-primary';
            case 'online':
                return 'label-info';
            default:
                return '';
            }
        }
    }
  }
</script>

<style scoped>
  .box {
    padding: 5px;
  }

  .panel.stats {
    border: 1px solid #ddd;
  }

  .metrics-banner-text {
    font-size: 1.8em;
    font-weight: 600;
    color: #ff7a7c;
  }

  .metrics-banner-text small {
    font-size: 80%;
    font-weight: 500;
    color: #666;
  }

  .chart-wrapper {
    padding: 0px!important;
    height: 310px!important;
  }

  .js-plotly-plot {
    overflow: hidden;
  }

  .plot-container,
  .svg-container {
    width: 100%!important;
    height: 100%!important;
    overflow: hidden;
  }


  .multiple-choices > div.formulate-input-group {
    height: 200px;
    max-height: 200px;
    overflow: auto;
    overscroll-behavior: contain;
    border: 1px solid #ddd;
    padding: 10px 10px;
    border-radius: 4px 4px 4px 4px;
  }

  .input-group {
    display: flex;
    flex-direction: row;
  }

  .input-group .formulate-input + .formulate-input {
    margin-left: .5em;
    align-self: center;
  }
 .schedule td {
   background: #ddd;
   padding: 1em;
  }

  .schedule .schedule-slot {
    width: 100%;
    border-radius: 4px 4px 4px 4px;
    background: #fff;
    text-align: center;
    padding: 1em;
  }
  .schedule td .schedule-slot + .schedule-slot {
    margin-top: 1em;
  }

  .schedule .schedule-slot div {
    width: 100%;
}
  .schedule .schedule-slot .schedule-slot-header {
    margin: -1em -1em 1em auto;
  }

  .schedule tr td {
    background: #ecebeb;
  }

  .schedule tr.add-row td {
    background: #f7f7f7;
  }
  .schedule .schedule-slot .schedule-slot-header button {
     background: transparent;
    border: 0;
    color: #222;
  }

  .schedule .schedule-custom-slots {
    display: flex;
    gap: 1em;
    padding: 1em;
  }
  .schedule .schedule-custom-slots .custom-slot {
    background-color: #f5fff0;
    border: 1px solid #ccc;
    flex-basis: 25%;
    flex-grow: 0;
    flex-shrink: 0;
  }
  .schedule .schedule-custom-slots .custom-slot.not-available {
    background-color: #fff0f0;
  }
  .zipcode-check {
    display: flex;
    gap: 2rem;
  }
</style>



// WEBPACK FOOTER //
// AgentModal.vue?4066fb24